import { useCallback, useMemo } from 'react'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import memoizeOne from 'memoize-one'
import { DialogSchema } from '@feedyou/schema'

import { slugify } from '../../../../../lib/transliterate'
import { useDialogs } from '../../../../../store/dialog'
import { isValidDialogId } from '../../../../../lib/regex'
import { dialogOptionsSelector } from './selectors'
import { toLowerCase } from '../../../../../lib/string'
import { DANGER__slugifyMemoized, DANGER__toLowerCaseMemoized } from '../../../../../lib/memo'

// This memoization is necessary, because for large bots (thousands of dialogs), calling all
// those callbacks for each option over and over again clogs CPU and makes select unusable, especially on slower computers.
export const useMemoizedDialogSelectData = () => {
  const dialogs = useDialogs()
  const dialogOptions = useSelector(dialogOptionsSelector)

  const getDialogDescriptorFromOptionValue = (value: string | null) => {
    if (!value) return null
    return { id: slugify(value), name: value }
  }

  const slugifyInputValueMemoized = useMemo(() => memoizeOne(slugify), [])
  const getLowercaseInputValueMemoized = useMemo(() => memoizeOne(toLowerCase), [])

  const inputValueIsIncludedInDialogId = useMemoizedCriteria(
    useCallback(
      (dialogId, inputValue) =>
        DANGER__slugifyMemoized(dialogId).includes(slugifyInputValueMemoized(inputValue)),
      [DANGER__slugifyMemoized, slugifyInputValueMemoized]
    )
  )

  const inputValueIsIncludedInDialogName = useMemoizedCriteria(
    useCallback(
      (dialogName, inputValue) =>
        !!dialogName &&
        !!inputValue &&
        DANGER__toLowerCaseMemoized(dialogName).includes(
          getLowercaseInputValueMemoized(inputValue)
        ),
      [DANGER__toLowerCaseMemoized, getLowercaseInputValueMemoized]
    )
  )

  const isValidNewOption = useMemo(
    () =>
      memoizeOne((inputValue: string) => {
        const descriptor = getDialogDescriptorFromOptionValue(inputValue)

        if (!descriptor) {
          return false
        }

        const { name, id } = descriptor

        if (!isValidDialogId(id)) {
          return false
        }

        return Object.values(dialogs).every((dialog: DialogSchema) => {
          const nameAlreadyTaken =
            !!dialog['name'] &&
            DANGER__toLowerCaseMemoized(dialog['name']) === getLowercaseInputValueMemoized(name)

          const idAlreadyTaken =
            DANGER__toLowerCaseMemoized(dialog['id']) === slugifyInputValueMemoized(id)

          return !nameAlreadyTaken && !idAlreadyTaken
        })
      }),
    [
      dialogs,
      getLowercaseInputValueMemoized,
      slugifyInputValueMemoized,
      DANGER__toLowerCaseMemoized
    ]
  )

  return {
    dialogOptions,
    // Binding only the used args to memoized functions to avoid breaking cache by unused args
    // being supplied when the function is called as a callback.
    isValidNewOption: (inputValue: string) => isValidNewOption(inputValue),

    inputValueMatchesDialogId: (inputValue: string, dialogId: string) =>
      inputValueIsIncludedInDialogId(inputValue, dialogId),

    inputValueMatchesDialogName: (inputValue: string, dialogName?: string) =>
      !!dialogName && inputValueIsIncludedInDialogName(inputValue, dialogName),

    getDialogDescriptorFromOptionValue
  }
}

// For each dialogPropertyValue, a new memoized function (with cache size 1) is created
const useMemoizedCriteria = (
  criteria: (dialogPropertyValue: string, inputValue: string) => boolean
) => {
  return useMemo(() => {
    const criteriaMemoized = _.memoize((dialogPropertyValue: string) => {
      return memoizeOne((inputValue: string) => criteria(dialogPropertyValue, inputValue))
    })

    return (inputValue: string, dialogId: string) => criteriaMemoized(dialogId)(inputValue)
  }, [criteria])
}
